package com.solevo.dist.components

import androidx.compose.runtime.*
import com.solevo.dist.theme.JsTheme
import com.varabyte.kobweb.compose.css.AlignItems
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.css.JustifyContent
import com.varabyte.kobweb.compose.css.Overflow
import com.varabyte.kobweb.compose.css.ScrollBehavior
import com.varabyte.kobweb.compose.css.Transition
import com.varabyte.kobweb.compose.foundation.layout.*
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Color.Companion.rgb
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.modifiers.onClick
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.silk.components.icons.fa.FaXmark
import com.varabyte.kobweb.silk.components.icons.fa.IconSize
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.breakpoint.Breakpoint
import com.varabyte.kobweb.silk.theme.breakpoint.rememberBreakpoint
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.SupervisorJob
import kotlinx.coroutines.delay
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.css.LineStyle
import org.jetbrains.compose.web.css.Position
import org.jetbrains.compose.web.css.ms
import org.jetbrains.compose.web.css.percent
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.css.rgba
import org.jetbrains.compose.web.css.vh

/**
 * NotificationsPanel - Overlay panel that slides in from the right
 * Similar to OverflowSidePanel but positioned on the right side
 */
@Composable
fun NotificationsPanel(
    isOpen: Boolean,
    onClose: () -> Unit,
    content: @Composable () -> Unit
) {
    val scope = remember { CoroutineScope(SupervisorJob() + Dispatchers.Default) }
    val breakpoint = rememberBreakpoint()

    var translateX by remember { mutableStateOf(100.percent) }
    var opacity by remember { mutableStateOf(0.percent) }

    LaunchedEffect(isOpen) {
        if (isOpen) {
            translateX = 0.percent
            opacity = 100.percent
        } else {
            translateX = 100.percent
            opacity = 0.percent
        }
    }

    if (!isOpen && opacity == 0.percent) return

    // Container Box to hold both backdrop and panel as siblings
    Box(
        modifier = Modifier
            .fillMaxWidth()
            .height(100.vh)
            .position(Position.Fixed)
            .top(0.px)
            .left(0.px)
            .zIndex(10)
    ) {
        // Backdrop overlay
        Box(
            modifier = Modifier
                .fillMaxWidth()
                .fillMaxHeight()
                .position(Position.Fixed)
                .top(0.px)
                .left(0.px)
                .opacity(opacity)
                .transition(Transition.of(property = "opacity", duration = 300.ms))
                .backgroundColor(rgba(0, 0, 0, 0.3))
                .cursor(Cursor.Pointer)
                .onClick {
                    scope.launch {
                        translateX = 100.percent
                        opacity = 0.percent
                        delay(300)
                        onClose()
                    }
                }
        )

        // Panel content - sibling to backdrop, not child
        Column(
            modifier = Modifier
                .fillMaxHeight()
                .width(if (breakpoint < Breakpoint.MD) 90.percent else 400.px)
                .position(Position.Fixed)
                .right(0.px)
                .top(0.px)
                .translateX(translateX)
                .transition(Transition.of(property = "translate", duration = 300.ms))
                .overflow(Overflow.Auto)
                .scrollBehavior(ScrollBehavior.Smooth)
                .backgroundColor(JsTheme.White.rgb)
                .zIndex(11)
        ) {
            // Header with close button
            Row(
                modifier = Modifier
                    .fillMaxWidth()
                    .padding(24.px)
                    .borderBottom(1.px, LineStyle.Solid, rgb(230, 232, 235))
                    .justifyContent(JustifyContent.SpaceBetween)
                    .alignItems(AlignItems.Center)
            ) {
                SpanText(
                    "Notifications",
                    Modifier
                        .fontSize(24.px)
                        .fontWeight(com.varabyte.kobweb.compose.css.FontWeight.Bold)
                )
                FaXmark(
                    modifier = Modifier
                        .color(rgb(120, 130, 140))
                        .cursor(Cursor.Pointer)
                        .onClick {
                            scope.launch {
                                translateX = 100.percent
                                opacity = 0.percent
                                delay(300)
                                onClose()
                            }
                        },
                    size = IconSize.LG
                )
            }

            // Content
            content()
        }
    }
}

